// ─── In-memory JWT token store ────────────────────────────────────────────────
// Access token lives in memory only — never in localStorage.
// Refresh token lives in an HttpOnly cookie (managed by the browser/server).
// localStorage is used ONLY for MY_USER_ID backward-compat hints (non-secret).

const BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001'

let _state = {
  accessToken: null,
  userId: null,        // 'user' or 'participant_N'
  role: null,          // 'owner' | 'participant' | null
  participantId: null, // numeric participant id or null
  displayName: null,
  actAs: null,         // owner impersonation: 'participant_N' or null
}

// Listeners notified on auth state changes
const _listeners = new Set()

export function onAuthChange(cb) {
  _listeners.add(cb)
  return () => _listeners.delete(cb)
}

function _notify() {
  _listeners.forEach(cb => {
    try { cb({ ..._state }) } catch {}
  })
}

// ── Write auth state ──────────────────────────────────────────────────────────

export function setAuth({ access_token, user_id, role, participant_id, display_name, is_owner }) {
  const resolvedRole = is_owner ? 'owner' : (role || 'participant')

  _state.accessToken = access_token
  _state.userId      = user_id || 'user'
  _state.role        = resolvedRole
  _state.participantId = participant_id || null
  _state.displayName   = display_name  || null
  _state.actAs         = null  // clear impersonation on new login

  // Mirror to localStorage so MY_USER_ID module-constant stays correct after F5
  if (resolvedRole === 'owner') {
    localStorage.setItem('dt_owner', 'true')
    if (participant_id) {
      localStorage.setItem('dt_owner_participant_id', String(participant_id))
    } else {
      localStorage.removeItem('dt_owner_participant_id')
    }
    localStorage.removeItem('dt_participant_id')
    localStorage.removeItem('dt_participant_name')
  } else {
    localStorage.removeItem('dt_owner')
    localStorage.removeItem('dt_owner_participant_id')
    if (participant_id) {
      localStorage.setItem('dt_participant_id', String(participant_id))
      localStorage.setItem('dt_participant_name', display_name || '')
    }
  }

  _notify()
}

export function clearAuth() {
  _state = { accessToken: null, userId: null, role: null, participantId: null, displayName: null, actAs: null }
  localStorage.removeItem('dt_owner')
  localStorage.removeItem('dt_owner_participant_id')
  localStorage.removeItem('dt_participant_id')
  localStorage.removeItem('dt_participant_name')
  _notify()
}

// ── Read auth state ───────────────────────────────────────────────────────────

export const getAccessToken  = () => _state.accessToken
export const getRole         = () => _state.role
export const isOwnerAuth     = () => _state.role === 'owner'
export const getParticipantId= () => _state.participantId
export const getDisplayName  = () => _state.displayName
export const getActAs        = () => _state.actAs

/** Returns effective user_id for API calls (actAs takes precedence) */
export function getMyUserId() {
  return _state.actAs || _state.userId || 'user'
}

// ── Impersonation (owner only) ────────────────────────────────────────────────

export function setActAs(userId) {
  if (_state.role !== 'owner') return
  _state.actAs = userId || null
  _notify()
}

// ── Network: refresh & logout ─────────────────────────────────────────────────

// Singleton promise so parallel calls wait for the same refresh
let _refreshPromise = null

/**
 * Try to restore session from the HttpOnly dt_refresh cookie.
 * Returns auth data on success, null on failure.
 */
export async function authRefresh() {
  if (_refreshPromise) return _refreshPromise
  _refreshPromise = _doRefresh().finally(() => { _refreshPromise = null })
  return _refreshPromise
}

async function _doRefresh() {
  try {
    const res = await fetch(`${BASE_URL}/api/auth/refresh`, {
      method: 'POST',
      credentials: 'include',  // send HttpOnly cookie
      headers: { 'Content-Type': 'application/json' },
    })
    if (!res.ok) return null
    const data = await res.json()
    if (data.ok && data.access_token) {
      setAuth(data)
      return data
    }
    return null
  } catch {
    return null
  }
}

export async function authLogout() {
  try {
    await fetch(`${BASE_URL}/api/auth/logout`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(_state.accessToken ? { Authorization: `Bearer ${_state.accessToken}` } : {}),
      },
    })
  } catch {}
  clearAuth()
}
